import { useCallback, useEffect, useState } from 'react';
/* ⛔ **As quatro primeiras folhas vêm ANTES de qualquer componente** — 18/09/2026.
   O menu da conta ("Quem entra no Hub") e o compromisso passaram a usar as
   peças do passo a passo, que trazem a folha de Imóveis junto. Importados
   antes das páginas, levavam 312 regras para o TOPO do CSS, na frente de
   `clientes.css` e `sistema.css` — e empate de seletor é ganho por quem vem
   depois. Aqui, na ordem em que o Calendário sempre as pediu; a segunda
   importação da mesma folha não a move. */
import './estilos/calendario.css';
import './estilos/clientes.css';
import './estilos/sistema.css';
import './estilos/calendario-2026.css';
import { Avisos } from './componentes/Avisos.tsx';
import { BarraSuperior } from './componentes/BarraSuperior.tsx';
import { Lateral } from './componentes/Lateral.tsx';
import { TrocarSenha } from './componentes/TrocarSenha.tsx';
import { Usuarios } from './componentes/Usuarios.tsx';
import { api, type Usuario } from './lib/api.ts';
import { esvaziarFilas } from './lib/fila-de-matriculas.ts';
import { definirPapel } from './lib/papel.ts';
import { usarRota } from './lib/rota.ts';
import { Calendario } from './paginas/Calendario.tsx';
import { Clientes } from './paginas/Clientes.tsx';
import { Comercial } from './paginas/Comercial.tsx';
import { FichaDaProspeccao } from './paginas/FichaDaProspeccao.tsx';
import { FichaCliente } from './paginas/FichaCliente.tsx';
/* ⚠️ **A ordem destas importações decide a ordem do CSS** — 14/09/2026. O
   Vite junta as folhas de estilo na ordem em que as páginas aparecem aqui, e
   empate de seletor é ganho por quem vem depois. A tela Hoje importa
   `sistema.css`; vindo antes da ficha, ela o puxava para antes de
   `clientes.css`, e a ficha do cliente mudava 6px sem ninguém mexer nela. */
import { Hoje } from './paginas/Hoje.tsx';
import { Financeiro } from './paginas/Financeiro.tsx';
import { Relatorios } from './paginas/Relatorios.tsx';
/* ⭐ O Marketing (15/10/2026) vem DEPOIS de todas as páginas de trabalho: a
   folha dele (`marketing.css`) entra no fim, e com o prefixo `mkt-` não
   alcança regra de ninguém. As que ele importa além dela (`sistema.css`, as
   do passo a passo) já vieram antes, e a segunda importação não as move. */
import { Marketing } from './paginas/Marketing.tsx';
/* ⭐ A ficha do post (parte 2, 15/10/2026) vem logo depois do Marketing: importa as mesmas folhas, e nenhuma nova. */
import { FichaDoPost } from './paginas/FichaDoPost.tsx';
import { AceitarConvite } from './paginas/AceitarConvite.tsx';
import { Login } from './paginas/Login.tsx';
import { RedefinirSenha } from './paginas/RedefinirSenha.tsx';
import './estilos/app.css';
/* ⭐ A casca do sistema visual 2026 — menu lateral e barra superior, 14/09/2026. */
import './estilos/casca.css';
/* ⭐ A pele nova, só na ficha do caso — decisão 20, 13/09/2026. Depois de app.css, para vencer. */

type Estado = { fase: 'conferindo' } | { fase: 'fora' } | { fase: 'dentro'; usuario: Usuario };

/**
 * Os dois links que chegam por e-mail: `/?redefinir=TOKEN` e `/?convite=TOKEN`.
 *
 * Não vale um roteador inteiro para duas rotas. O que muda entre elas é o
 * texto e a rota da API — o caminho é o mesmo: confere o link, mostra o
 * formulário, e limpa a barra de endereços no fim.
 */
function linkDoEmail(): { tipo: 'redefinir' | 'convite'; token: string } | null {
  const busca = new URLSearchParams(window.location.search);
  const redefinir = busca.get('redefinir');
  if (redefinir) return { tipo: 'redefinir', token: redefinir };
  const convite = busca.get('convite');
  if (convite) return { tipo: 'convite', token: convite };
  return null;
}

export function App() {
  const [estado, setEstado] = useState<Estado>({ fase: 'conferindo' });
  const [link, setLink] = useState(linkDoEmail);
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  const [vendoUsuarios, setVendoUsuarios] = useState(false);
  /*
   * ⭐ **O menu que se recolhe — 14/09/2026, pedido dele.**
   *
   * > *"Gostaria que a barra lateral se 'escondesse' e só quando eu passar o
   * > mouse sobre o 'filete' da barra, aí sim ela aparece. Ou então, ter
   * > aquela setinha/botãozinho para a barra recolher ou mostrar."* — Richard
   *
   * ⭐ **As duas coisas juntas:** o botão recolhe e fixa; recolhido, sobra um
   * filete na borda, e o mouse sobre ele abre o menu por cima da página, sem
   * empurrar nada. ⚠️ **Lembrado neste navegador**, como as seções abertas.
   */
  const [menuRecolhido, setMenuRecolhido] = useState(
    () => localStorage.getItem('kairos-menu') === 'recolhido',
  );
  function alternarMenu() {
    setMenuRecolhido((r) => {
      localStorage.setItem('kairos-menu', r ? 'fixo' : 'recolhido');
      return !r;
    });
  }
  const { caminho, ir } = usarRota();
  const token = link?.token ?? null;

  // Ao abrir, pergunta ao servidor se a sessão ainda vale. O cookie é
  // httpOnly: a tela não consegue — nem deve — ler isso sozinha.
  useEffect(() => {
    if (token) return;
    api
      .quemSouEu()
      .then((usuario) => setEstado({ fase: 'dentro', usuario }))
      .catch(() => setEstado({ fase: 'fora' }));
  }, [token]);

  // O papel decide o que a tela mostra. Sai junto com a sessão: aba deixada
  // aberta depois do "sair" não pode continuar exibindo botão de master.
  useEffect(() => {
    definirPapel(estado.fase === 'dentro' ? estado.usuario.papel : null);
    /* ⭐ §70: ao sair (ou a sessão expirar), nenhuma leitura de matrícula fica na memória da aba. */
    if (estado.fase !== 'dentro') esvaziarFilas();
  }, [estado]);

  const sair = useCallback(async () => {
    await api.sair().catch(() => undefined);
    setEstado({ fase: 'fora' });
  }, []);

  /** Sessão que expirou enquanto a tela estava aberta cai aqui. */
  const expirou = useCallback(() => setEstado({ fase: 'fora' }), []);

  /** Fim do caminho dos dois links de e-mail. */
  const fecharLink = useCallback(() => {
    // Tira o token da barra de endereços: link usado não fica no histórico do
    // navegador para ser reaberto por engano.
    window.history.replaceState(null, '', window.location.pathname);
    setLink(null);
    setEstado({ fase: 'fora' });
  }, []);

  // O link do e-mail vem antes de tudo: quem chegou por ele não deve ver a
  // tela de entrada por baixo.
  if (link?.tipo === 'redefinir') {
    return <RedefinirSenha token={link.token} aoTerminar={fecharLink} />;
  }
  if (link?.tipo === 'convite') {
    return <AceitarConvite token={link.token} aoTerminar={fecharLink} />;
  }

  if (estado.fase === 'conferindo') {
    return <div className="carregando">Abrindo o KairósHub…</div>;
  }

  if (estado.fase === 'fora') {
    return <Login aoEntrar={(usuario) => setEstado({ fase: 'dentro', usuario })} />;
  }

  const clienteAberto = caminho.startsWith('/clientes/')
    ? caminho.slice('/clientes/'.length)
    : null;

  /*
   * O relatório tem endereço próprio — pedido do Richard, 16/08/2026: caso e
   * relatório ficam ligados e clicáveis nos dois sentidos.
   *
   * ⚠️ **Sem endereço próprio, o link do caso para o relatório não existiria.**
   * Guardar "qual relatório está aberto" só dentro da página de Relatórios
   * significa que ninguém consegue apontar para ele de fora — e era exatamente
   * o que a ficha do cliente precisava fazer.
   */
  const relatorioAberto = caminho.startsWith('/relatorios/')
    ? caminho.slice('/relatorios/'.length)
    : null;
  /* ⭐ A ficha da empresa do funil é PÁGINA, como a do cliente (20/09/2026). */
  const empresaAberta = caminho.startsWith('/comercial/') ? caminho.slice('/comercial/'.length) : null;
  /* ⭐ E a do post também (parte 2 do Marketing, 15/10/2026). */
  const postAberto = caminho.startsWith('/marketing/') ? caminho.slice('/marketing/'.length) : null;

  return (
    <>
      <div className={'casca' + (menuRecolhido ? ' menu-recolhido' : '')}>
      <Lateral
        recolhido={menuRecolhido}
        aoAlternarMenu={alternarMenu}
        usuario={estado.usuario}
        caminho={caminho}
        aoNavegar={ir}
        aoTrocarSenha={() => setTrocandoSenha(true)}
        aoCriarUsuario={() => setVendoUsuarios(true)}
        aoSair={sair}
      />
      <div className="area">
      <BarraSuperior
        caminho={caminho}
        aoNavegar={ir}
        menuRecolhido={menuRecolhido}
        aoMostrarMenu={alternarMenu}
      />
      <main className="pagina">
        {clienteAberto ? (
          <FichaCliente
            id={clienteAberto}
            aoVoltar={() => ir('/clientes')}
            aoAbrirRelatorio={(id) => ir(`/relatorios/${id}`)}
            aoExpirarSessao={expirou}
          />
        ) : caminho === '/clientes' ? (
          <Clientes aoAbrirCliente={(id) => ir(`/clientes/${id}`)} aoExpirarSessao={expirou} />
        ) : empresaAberta ? (
          <FichaDaProspeccao
            id={empresaAberta}
            ehMaster={estado.usuario.papel === 'master'}
            aoVoltar={() => ir('/comercial')}
            aoAbrirCliente={(id) => ir(`/clientes/${id}`)}
            aoExpirarSessao={expirou}
          />
        ) : caminho === '/comercial' ? (
          <Comercial
            ehMaster={estado.usuario.papel === 'master'}
            aoAbrirEmpresa={(id) => ir(`/comercial/${id}`)}
            aoExpirarSessao={expirou}
          />
        ) : caminho === '/calendario' ? (
          <Calendario aoExpirarSessao={expirou} />
        ) : /* O Financeiro é do master. O servidor é quem garante (403); aqui
               só se evita abrir uma tela que nasceria em erro, para quem
               chegou digitando o endereço na barra. */
        caminho === '/financeiro' && estado.usuario.papel === 'master' ? (
          <Financeiro
            aoAbrirCliente={(id) => ir(`/clientes/${id}`)}
            aoExpirarSessao={expirou}
          />
        ) : /* ⭐ O Marketing é do master, como o Financeiro (PRD §50.1): quem
               garante é o servidor (403); aqui só não se abre a tela que
               nasceria em erro para quem digitou o endereço. */
        postAberto && estado.usuario.papel === 'master' ? (
          <FichaDoPost key={postAberto} id={postAberto} aoVoltar={() => ir('/marketing')} aoNavegar={ir} aoExpirarSessao={expirou} />
        ) : caminho === '/marketing' && estado.usuario.papel === 'master' ? (
          <Marketing aoExpirarSessao={expirou} aoAbrirPost={(id) => ir(`/marketing/${id}`)} />
        ) : /* ⭐ PRD §75 (29/10/2026): a tela dos modelos de IA saiu; o comitê automático decide sem tela. */
        caminho === '/relatorios' || relatorioAberto ? (
          <Relatorios
            aberto={relatorioAberto}
            aoAbrir={(id) => ir(id ? `/relatorios/${id}` : '/relatorios')}
            aoAbrirCliente={(id) => ir(`/clientes/${id}`)}
            aoExpirarSessao={expirou}
          />
        ) : (
          <Hoje aoExpirarSessao={expirou} aoAbrirCalendario={() => ir('/calendario')} />
        )}
      </main>
      </div>
      </div>
      {trocandoSenha && <TrocarSenha aoFechar={() => setTrocandoSenha(false)} />}
      {vendoUsuarios && (
        <Usuarios eu={estado.usuario.id} aoFechar={() => setVendoUsuarios(false)} />
      )}
      <Avisos />
    </>
  );
}
